// PriceLookup — Records / Reception price-quote page.
//
// Use case: a caller asks "how much does X cost?" and the front-desk
// staff needs an answer in 5 seconds. The page is built around that
// single task — three tabs (Labs / Scans / Services), a fat search bar
// that filters as you type, a result list, and when you click a row,
// the price jumps out in a large card at the top so it's easy to read
// off the screen while still on the call.
//
// Read-only by design — no editing, no insurance picker, no patient
// context. Records staff aren't pricing a specific patient's bill;
// they're quoting a list price.

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, InputAdornment,
  ToggleButtonGroup, ToggleButton, Stack, Chip, Avatar, alpha,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  CircularProgress, Alert, IconButton, Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Science as LabIcon,
  CameraAlt as ScanIcon,
  MedicalServices as ServiceIcon,
  Refresh as RefreshIcon,
  AttachMoney as MoneyIcon,
  Schedule as ClockIcon,
  ContentCopy as CopyIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import axios from 'axios';
import {
  recordPricesLabs, recordPricesScans, recordPricesServices,
} from '../BaseURL';

// One catalog config per tab. Adding a fourth tab (e.g. Procedures) is
// one entry here + one backend endpoint — the rest of the page is
// data-driven.
const TABS = [
  { id: 'labs',     label: 'Labs',     url: recordPricesLabs,     icon: <LabIcon />,    color: '#0e926a' },
  { id: 'scans',    label: 'Scans',    url: recordPricesScans,    icon: <ScanIcon />,   color: '#5468FF' },
  { id: 'services', label: 'Services', url: recordPricesServices, icon: <ServiceIcon/>, color: '#b45309' },
];

const fmtMoney = (n) => `GH₵ ${Number(n || 0).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Copy a string to the clipboard. Used by the "Copy price" button so
// staff can paste the quote into a chat/email without retyping.
async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (_) {
    return false;
  }
}

export default function PriceLookup() {
  const [tab, setTab]           = useState('labs');
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [copied, setCopied]     = useState(false);

  const activeTab = TABS.find((t) => t.id === tab) || TABS[0];

  // Load the catalog when the tab changes. Each tab caches its own rows
  // in component state — switching tabs and back is instant.
  const load = useCallback(async () => {
    setLoading(true); setError('');
    setSelectedRow(null);
    try {
      const res = await axios.get(activeTab.url);
      if (res.data?.success) {
        setRows(Array.isArray(res.data.rows) ? res.data.rows : []);
      } else {
        setError(res.data?.message || 'Failed to load prices.');
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Network error.');
    } finally {
      setLoading(false);
    }
  }, [activeTab.url]);

  useEffect(() => { load(); }, [load]);

  // Filtered rows — substring match on name + code, case-insensitive.
  // Cheap enough at catalog sizes (~hundreds of rows) that we do it
  // every render instead of memoising elaborately.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const name = String(r.name || '').toLowerCase();
      const code = String(r.code || '').toLowerCase();
      return name.includes(q) || code.includes(q);
    });
  }, [rows, search]);

  const handleCopy = async () => {
    if (!selectedRow) return;
    const text = `${selectedRow.name}: ${fmtMoney(selectedRow.cash)} (cash). Insurance top-up ${fmtMoney(selectedRow.topup)}.`;
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <Box sx={{
      p: { xs: 2, md: 3 },
      minHeight: '100vh',
      // Use the theme's surface tokens instead of a hardcoded light
      // gradient — the previous version rendered as bright white on
      // dark mode, hiding every label on top of it.
      bgcolor: 'background.default',
      color: 'text.primary',
    }}>
      {/* Page header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Avatar sx={{
          bgcolor: alpha(activeTab.color, 0.18),
          color: activeTab.color,
          border: `1px solid ${alpha(activeTab.color, 0.3)}`,
          width: 48, height: 48,
        }}>
          <MoneyIcon />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, lineHeight: 1.15, color: 'text.primary' }}>
            Price Lookup
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Quote a lab, scan, or service price in seconds — search above, tap a row to copy.
          </Typography>
        </Box>
        <IconButton onClick={load} title="Reload" sx={{ border: '1px solid', borderColor: 'divider', color: 'text.primary' }}>
          <RefreshIcon />
        </IconButton>
      </Stack>

      {/* Tab pills */}
      <ToggleButtonGroup
        value={tab}
        exclusive
        onChange={(_, v) => v && setTab(v)}
        sx={{
          mb: 2,
          '& .MuiToggleButton-root': {
            textTransform: 'none', fontWeight: 700, px: 2.5, py: 1,
            borderRadius: '999px !important', border: '1.5px solid', borderColor: 'divider',
            mr: 1,
          },
        }}
      >
        {TABS.map((t) => (
          <ToggleButton
            key={t.id} value={t.id}
            sx={{
              color: t.color,
              '&.Mui-selected': {
                bgcolor: t.color, color: 'white',
                borderColor: t.color,
                '&:hover': { bgcolor: t.color, color: 'white' },
              },
            }}
          >
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              {t.icon} {t.label}
            </Box>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {/* Big price callout — visible whenever a row is selected. This is
          the "answer the phone in 5 seconds" surface. */}
      {selectedRow && (
        <Card sx={{
          mb: 2,
          background: `linear-gradient(135deg, ${activeTab.color} 0%, ${alpha(activeTab.color, 0.75)} 100%)`,
          color: 'white',
          boxShadow: `0 8px 24px ${alpha(activeTab.color, 0.35)}`,
        }}>
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, opacity: 0.85, letterSpacing: 1, textTransform: 'uppercase' }}>
                  {activeTab.label.replace(/s$/, '')} — selected
                </Typography>
                <Typography sx={{ fontSize: { xs: '1.3rem', sm: '1.7rem' }, fontWeight: 800, lineHeight: 1.2, mt: 0.5 }}>
                  {selectedRow.name}
                </Typography>
                {selectedRow.code && (
                  <Typography variant="caption" sx={{ opacity: 0.85 }}>
                    Code: {selectedRow.code}
                  </Typography>
                )}
                {selectedRow.waiting_days != null && Number(selectedRow.waiting_days) > 0 && (
                  <Chip
                    size="small"
                    icon={<ClockIcon sx={{ color: 'white !important', fontSize: 16 }} />}
                    label={`Result ready in ${selectedRow.waiting_days} day${Number(selectedRow.waiting_days) === 1 ? '' : 's'}`}
                    sx={{
                      mt: 1,
                      bgcolor: 'rgba(255,255,255,0.18)',
                      color: 'white',
                      fontWeight: 700,
                    }}
                  />
                )}
              </Box>
              <Stack direction="row" spacing={3} alignItems="center">
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, opacity: 0.85, letterSpacing: 1, textTransform: 'uppercase' }}>
                    Cash
                  </Typography>
                  <Typography sx={{ fontSize: { xs: '1.6rem', sm: '2.2rem' }, fontWeight: 800, lineHeight: 1.1 }}>
                    {fmtMoney(selectedRow.cash)}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.25)' }} />
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, opacity: 0.85, letterSpacing: 1, textTransform: 'uppercase' }}>
                    Insurance top-up
                  </Typography>
                  <Typography sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' }, fontWeight: 700, lineHeight: 1.1, opacity: 0.95 }}>
                    {fmtMoney(selectedRow.topup)}
                  </Typography>
                </Box>
              </Stack>
              <IconButton
                onClick={handleCopy}
                title="Copy quote to clipboard"
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.15)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' },
                }}
              >
                <CopyIcon />
              </IconButton>
            </Stack>
            {copied && (
              <Typography sx={{ mt: 1, fontSize: '0.8rem', opacity: 0.95 }}>
                ✓ Copied to clipboard — paste it into a chat or email.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <Card sx={{ mb: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <TextField
            fullWidth
            placeholder={`Type a ${activeTab.label.toLowerCase().replace(/s$/, '')} name or code — results filter as you type`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: activeTab.color }} />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}><ClearIcon /></IconButton>
                </InputAdornment>
              ),
              // Force readable input color in both themes — the default
              // dark TextField in some app themes inherits a near-black
              // text colour on a near-black background.
              sx: { fontSize: '1.05rem', fontWeight: 600, py: 0.5, color: 'text.primary' },
            }}
          />
        </CardContent>
      </Card>

      {/* Result list */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && rows.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : filtered.length === 0 && !error ? (
        // Don't double up with the error alert above. If the load
        // failed, the user already sees "Failed to load …" — showing
        // "No labs in the catalog yet" on top of it is misleading.
        <Alert severity="info">
          {search.trim()
            ? `No ${activeTab.label.toLowerCase()} match "${search.trim()}".`
            : `No ${activeTab.label.toLowerCase()} in the catalog yet.`}
        </Alert>
      ) : filtered.length === 0 ? null : (
        <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: 'background.paper' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {/* Header cells get an accent-tinted overlay on top of
                    the theme's surface so they're visible in both light
                    and dark mode. */}
                <TableCell sx={{ fontWeight: 800, bgcolor: alpha(activeTab.color, 0.12), color: 'text.primary' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 800, bgcolor: alpha(activeTab.color, 0.12), color: 'text.primary' }}>Code</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800, bgcolor: alpha(activeTab.color, 0.12), color: 'text.primary' }}>Cash</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800, bgcolor: alpha(activeTab.color, 0.12), color: 'text.primary' }}>Insurance top-up</TableCell>
                <TableCell sx={{ fontWeight: 800, bgcolor: alpha(activeTab.color, 0.12), color: 'text.primary' }}>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((r, i) => {
                const selected = selectedRow && selectedRow.name === r.name && selectedRow.code === r.code;
                return (
                  <TableRow
                    key={`${r.code || ''}_${r.name}_${i}`}
                    hover
                    selected={selected}
                    onClick={() => setSelectedRow(r)}
                    sx={{
                      cursor: 'pointer',
                      '&.Mui-selected': { bgcolor: alpha(activeTab.color, 0.10) },
                      '&.Mui-selected:hover': { bgcolor: alpha(activeTab.color, 0.14) },
                    }}
                  >
                    <TableCell sx={{ fontWeight: selected ? 700 : 500 }}>{r.name}</TableCell>
                    <TableCell><Typography variant="caption" sx={{ color: 'text.secondary' }}>{r.code || '—'}</Typography></TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: activeTab.color }}>{fmtMoney(r.cash)}</TableCell>
                    <TableCell align="right">{fmtMoney(r.topup)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        {r.waiting_days != null && Number(r.waiting_days) > 0 && (
                          <Chip
                            size="small"
                            icon={<ClockIcon sx={{ fontSize: 14 }} />}
                            label={`${r.waiting_days}d`}
                            sx={{ height: 20, fontWeight: 700, fontSize: '0.7rem' }}
                          />
                        )}
                        {r.age_group && (
                          <Chip size="small" label={r.age_group} sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                        {r.insurance_status && (
                          <Chip
                            size="small"
                            label={r.insurance_status}
                            sx={{
                              height: 20, fontSize: '0.7rem',
                              bgcolor: alpha(activeTab.color, 0.12),
                              color: activeTab.color,
                              fontWeight: 700,
                            }}
                          />
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        Showing {filtered.length} of {rows.length} {activeTab.label.toLowerCase()}.
      </Typography>
    </Box>
  );
}
